import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./VendorKnowledge.module.css";

const VendorKnowledge = () => {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [corpora, setCorpora] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [locationsInput, setLocationsInput] = useState(""); // Replaced location with locationsInput
  const [locations, setLocations] = useState([]); // New state for locations array
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [uploadCorpusId, setUploadCorpusId] = useState("");
  const [viewMode, setViewMode] = useState("card"); // 'card' or 'table'
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const userRole = localStorage.getItem("role");
  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // New function to format locations
  const formatLocation = (location) => {
    if (!location) return "";
    return capitalize(location.trim());
  };

  // Check authentication and set axios default headers
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      setIsAuthenticated(true);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      setIsAuthenticated(false);
      delete axios.defaults.headers.common["Authorization"];
      // navigate('/login');
    }
  }, [navigate]);

  // Fetch all corpora on component mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchCorpora();
    }
  }, [isAuthenticated]);

  const fetchCorpora = async () => {
    try {
      const Id = localStorage.getItem("userId");
      const response = await axios.get(
        userRole === "vendor"
          ? `https://prod.toxboxai.com/api/vendorknowledge/get-corpus/${Id}`
          : `https://prod.toxboxai.com/api/vendorknowledge/get-assigned-corpus/${Id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setCorpora(response.data.results || []);
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Unauthorized: Please log in again");
        localStorage.removeItem("jwtToken");
        setIsAuthenticated(false);
        // navigate('/login');
      } else {
        setError("Failed to fetch corpora");
      }
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type !== "application/pdf") {
      setError("Please select a valid PDF file");
      setFile(null);
      return;
    }
    setFile(selectedFile);
    setStatus("");
    setError("");
  };

  const handleCreateUpload = async () => {
    if (!file) {
      setError("Please select a PDF file");
      return;
    }
    if (!name || !description) {
      setError("Please provide both name and description");
      return;
    }

    setIsLoading(true);
    setError("");
    setStatus("");

    const formData = new FormData();
    formData.append("pdf", file);
    formData.append("name", name);
    formData.append("description", description);
    formData.append(
      "locations",
      JSON.stringify(locations.length > 0 ? locations : [])
    ); // Send locations array

    try {
      const response = await axios.post(
        "https://prod.toxboxai.com/api/vendorknowledge/upload-pdf",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setStatus(
        `PDF uploaded successfully! Corpus ID: ${response.data.corpusId}, S3 URL: ${response.data.s3Url}`
      );
      setFile(null);
      setName("");
      setDescription("");
      setLocationsInput("");
      setLocations([]);
      setShowCreateModal(false);
      await fetchCorpora(); // Refresh corpora list
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Unauthorized: Please log in again");
        localStorage.removeItem("jwtToken");
        setIsAuthenticated(false);
      } else {
        setError(err.response?.data?.error || "Failed to upload PDF");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleExistingUpload = async () => {
    if (!file) {
      setError("Please select a PDF file");
      return;
    }
    if (!name || !description || !uploadCorpusId) {
      setError("Please provide name, description, and select a corpus");
      return;
    }

    setIsLoading(true);
    setError("");
    setStatus("");

    const formData = new FormData();
    formData.append("pdf", file);
    formData.append("name", name);
    formData.append("description", description);
    formData.append("corpusId", uploadCorpusId);
    formData.append(
      "locations",
      JSON.stringify(locations.length > 0 ? locations : [])
    ); // Send locations array

    try {
      const response = await axios.post(
        "https://prod.toxboxai.com/api/vendorknowledge/upload-pdf-with-corpus",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setStatus(
        `PDF uploaded successfully! Corpus ID: ${response.data.corpusId}, Document ID: ${response.data.documentId}, S3 URL: ${response.data.s3Url}`
      );
      setFile(null);
      setName("");
      setDescription("");
      setUploadCorpusId("");
      setLocationsInput("");
      setLocations([]);
      setShowUploadModal(false);
      await fetchCorpora(); // Refresh corpora list
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Unauthorized: Please log in again");
        localStorage.removeItem("jwtToken");
        setIsAuthenticated(false);
      } else {
        setError(err.response?.data?.error || "Failed to upload PDF");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle locations input change
  const handleLocationsInputChange = (e) => {
    const input = e.target.value;
    setLocationsInput(input);
    const locationsArray = input
      .split(",")
      .map((loc) => formatLocation(loc))
      .filter((loc) => loc !== "");
    setLocations(locationsArray);
  };

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h2>Please Log In</h2>
          <button className={styles.createBtn}>Log In</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <input
          type="text"
          placeholder="Filter corpora..."
          className={styles.filter}
        />
        <div>
          {userRole === "vendor" && (
            <div className={styles.buttonGroup}>
              <button
                className={styles.createBtn}
                onClick={() => setShowCreateModal(true)}
              >
                Create Knowledge Base
              </button>
              <button
                className={styles.createBtn}
                onClick={() => setShowUploadModal(true)}
              >
                Upload to Existing Knowledge Base
              </button>
            </div>
          )}
        </div>
      </div>
      {/* 
      <div className={styles.viewToggle}>
        <button
          className={`${styles.cardbutton} ${viewMode === "card" ? styles.activeView : ""
            }`}
          onClick={() => setViewMode("card")}
        >
          Card View
        </button>
        <button
          className={`${styles.tablebutton} ${viewMode === "table" ? styles.activeView : ""
            }`}
          onClick={() => setViewMode("table")}
        >
          Table View
        </button>
        <input
          type="text"
          placeholder="Search corpora..."
          className={styles.search}
        />
      </div> */}

      {showCreateModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Create Knowledge Base</h2>
              <button
                className={styles.closeBtn}
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.form}>
              <div className={styles.formGroup}>
                <label>Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Knowledge Base Name"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Locations</label>
                <input
                  type="text"
                  value={locationsInput}
                  onChange={handleLocationsInputChange}
                  placeholder="Enter locations (e.g., New york, San francisco)"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Upload PDF</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                />
              </div>
              {error && <div className={styles.error}>{error}</div>}
              {status && <div className={styles.success}>{status}</div>}
              <div className={styles.formActions}>
                <button onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button
                  onClick={handleCreateUpload}
                  disabled={isLoading || !file || !name || !description}
                >
                  {isLoading ? "Uploading..." : "Upload PDF"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Upload to Existing Knowledge Base</h2>
              <button
                className={styles.closeBtn}
                onClick={() => setShowUploadModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.form}>
              <div className={styles.formGroup}>
                <label>Select Knowledge Base</label>
                <select
                  value={uploadCorpusId}
                  onChange={(e) => setUploadCorpusId(e.target.value)}
                >
                  <option value="">Select a corpus</option>
                  {corpora.map((corpus) => (
                    <option key={corpus.corpusId} value={corpus.corpusId}>
                      {corpus.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Source Name"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Source Description"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Locations</label>
                <input
                  type="text"
                  value={locationsInput}
                  onChange={handleLocationsInputChange}
                  placeholder="Enter locations (e.g., New york, San francisco)"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Upload PDF</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                />
              </div>
              {error && <div className={styles.error}>{error}</div>}
              {status && <div className={styles.success}>{status}</div>}
              <div className={styles.formActions}>
                <button onClick={() => setShowUploadModal(false)}>
                  Cancel
                </button>
                <button
                  onClick={handleExistingUpload}
                  disabled={
                    isLoading ||
                    !file ||
                    !name ||
                    !description ||
                    !uploadCorpusId
                  }
                >
                  {isLoading ? "Uploading..." : "Upload PDF"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewMode === "card" ? (
        <div className={styles.cardContainer}>
          {corpora.length > 0 ? (
            corpora.map((corpus) => (
              <div key={corpus.corpusId} className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3>{corpus.name}</h3>
                  <span className={styles.typeBadge}>
                    {corpus.stats?.status}
                  </span>
                </div>
                <div className={styles.cardBody}>
                  <p>
                    <strong>Description:</strong> {corpus.description}
                  </p>
                  <p className={styles.cardLocations}>
                    <strong>Locations:</strong>{" "}
                    {Array.isArray(corpus.locations) &&
                      corpus.locations.length > 0
                      ? corpus.locations.join(", ")
                      : "No locations"}
                  </p>
                </div>
                <div className={styles.cardFooter}>
                  {userRole === "vendor" && (
                    <button
                      className={styles.viewBtn}
                      onClick={() => navigate(`/sources/${corpus.corpusId}`)}
                    >
                      View Details
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className={styles.noData}>No knowledge bases found.</p>
          )}
        </div>
      ) : (
        <div className={styles.dataTableWrapper}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                {/* <th>Corpus ID</th> */}
                {/* <th>Created</th> */}
                <th>Status</th>
                <th className={styles.locations}>Locations</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {corpora.length > 0 ? (
                corpora.map((corpus) => (
                  <tr key={corpus.corpusId}>
                    <td>{corpus.name}</td>
                    <td>{corpus.description}</td>
                    {/* <td>{corpus.corpusId}</td> */}
                    {/* <td>{new Date(corpus.created).toLocaleString()}</td> */}
                    <td>{corpus?.stats?.status}</td>
                    <td className={styles.locations}>
                      {Array.isArray(corpus.locations) &&
                        corpus.locations.length > 0
                        ? corpus.locations.join(", ")
                        : "No locations"}
                    </td>
                    <td>
                      <button
                        className={styles.viewBtn}
                        onClick={() => navigate(`/sources/${corpus.corpusId}`)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className={styles.noData}>
                    No knowledge bases found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VendorKnowledge;
